import { isNotNil, isPositive } from 'ramda-adjunct';

import type { BudgetAmounts } from 'common/lib/budget/Adapters';
import type { PlanAmounts } from 'common/lib/planning';

import type { Maybe } from 'common/generated/graphql';
import { BudgetVariability, CategoryGroupType } from 'common/generated/graphql';
import type { Color, ThemeType } from 'common/types/Styles';

type MaybeAmountsLike = Partial<Omit<BudgetAmounts, 'rollover' | 'rolloverType'>> | undefined;

/**
 * This type is used to represent the types of category groups that are used in the Budget page.
 * It includes the types from the backend, plus the 'savings' type which is used to represent the Goals section.
 */
export type ExtendedCategoryGroupType = CategoryGroupType | 'savings';

export const getBudgetedAmountWithRollover = (budgeted: Maybe<number>, rollover: Maybe<number>) =>
  (budgeted ?? 0) + (rollover ?? 0);

export const getAvailableDisplayAmount = (
  amount: number | null | undefined,
  categoryType: ExtendedCategoryGroupType | null | undefined,
) => {
  if (categoryType === 'savings') {
    return amount || 0;
  }

  const available = amount ?? 0;
  // We want to avoid showing negative remaining for income categories
  if (categoryType === CategoryGroupType.INCOME) {
    return Math.max(0, available);
  }

  return available;
};

export const getColorForAvailableAmount = (
  available: number,
  categoryType: CategoryGroupType,
): Color => {
  if (available < 0 && categoryType === CategoryGroupType.EXPENSE) {
    return 'red';
  }

  return 'green';
};

export const getAmountColor = (amount: number | null | undefined, theme: ThemeType) => {
  if (amount === 0 || amount === undefined || amount === null) {
    return theme.color.grayDark;
  } else if (amount > 0) {
    return theme.color.green;
  } else {
    return theme.color.red;
  }
};

export const getDisplayProgressPercent = (actual: number, total: number) => {
  if (total !== 0) {
    if (actual * total < 0) {
      // actual and total have different signs
      return total > 0 ? 0 : 1;
    } else {
      return actual / total;
    }
  } else {
    return 0;
  }
};

export const getProgressBarColor = (type: string | undefined, isFlexBudgeting: boolean): Color => {
  switch (type) {
    case CategoryGroupType.INCOME:
    case CategoryGroupType.EXPENSE:
      return 'green';
    default:
      return 'blue'; // Goals
  }
};

export const getRemainingTextColor = (
  type: string | undefined,
  remaining: number | null | undefined,
  exceedsMonthlyProgress: boolean,
): Color => {
  if (type === 'savings') {
    return 'blueText';
  } else if (exceedsMonthlyProgress) {
    return remaining && remaining < 0 ? 'redText' : 'yellowText';
  } else if (isPositive(remaining)) {
    return 'greenText';
  }
  return 'textLight';
};

export const getGroupTypeActualAmountLabel = (type: string | undefined) => {
  switch (type) {
    case CategoryGroupType.INCOME:
      return 'earned';
    case CategoryGroupType.EXPENSE:
    case BudgetVariability.FLEXIBLE:
    case BudgetVariability.FIXED:
    case BudgetVariability.NON_MONTHLY:
      return 'spent';
    default:
      return 'contributed'; // Goals
  }
};

export const getRemainingAmount = (amounts: MaybeAmountsLike | PlanAmounts = {}) => {
  // We have different interfaces between mobile (BudgetAmounts) and web (PlanAmounts)
  // so the `remaining` field on web is called `available` on mobile.
  //
  // Once we have a unified interface we can remove this and just use `remaining` directly.
  const available = isWebPlanAmounts(amounts) ? amounts.remaining : amounts.available;

  if (isNotNil(available)) {
    // Rollovers are enabled, so remaining amount comes from backend
    return available;
  } else {
    // Rollovers are disabled, so remaining amount is calculated from budgeted and actual (old way)
    return (amounts.budgeted ?? 0) - (amounts.actual ?? 0);
  }
};

export const getColorForRemainingAmount = (
  amounts: MaybeAmountsLike,
  sectionType: ExtendedCategoryGroupType,
) => {
  const remaining = Math.round(getRemainingAmount(amounts));
  const categoryType = sectionType === 'savings' ? CategoryGroupType.INCOME : sectionType;
  return getColorForAvailableAmount(remaining, categoryType);
};

function isWebPlanAmounts(amounts: MaybeAmountsLike | PlanAmounts): amounts is PlanAmounts {
  return !!amounts && 'remaining' in amounts;
}

/**
 * Gets the max value for the progress bar of the groups in the Budget page.
 */
export const getGroupProgressBarMax = (amounts: PlanAmounts) => {
  // Actual is negative, so it's probably a category with refunds or something
  if (amounts?.actual && amounts?.actual < 0) {
    return 0;
  }

  const budgetedWithRollover = getBudgetedAmountWithRollover(amounts?.budgeted, amounts?.rollover);

  // If the budgeted with rollover is negative, we want any expense amount to fill up the bar.
  // Indicating that the user can't spend more. To achieve that, we set the max to 0.
  if (budgetedWithRollover && budgetedWithRollover < 0) {
    return 0;
  }

  return budgetedWithRollover;
};

/**
 * Formats a budget amount for display by:
 * 1. Rounding to nearest whole number
 * 2. Normalizing negative zero to zero
 */
export const formatBudgetAmount = (amount: number): number => {
  const roundedAmount = Math.round(amount);
  return Math.abs(roundedAmount) === 0 ? 0 : roundedAmount;
};
